import { APP_TYPE, ASSIGNMENT_TYPE } from '@/modules/app/constants/appConstants';
import subscriptionService from '@/modules/subscription/services/subscriptionService';
import logError from '@/utils/logError/logError';
import channelsService from '../services/channelsService';

const CORE_INTERNAL_APP_UID = 'CoreInternalApp';

const GROUP_STATE = {
  CONNECTED: 'connected',
  INSTALLED: 'installed',
  NOT_INSTALLED: 'not_installed',
};

// Lower index = higher in the list. Connected channels first, market apps last.
const GROUP_STATE_ORDER = {
  [GROUP_STATE.CONNECTED]: 0,
  [GROUP_STATE.INSTALLED]: 1,
  [GROUP_STATE.NOT_INSTALLED]: 2,
};

/**
 * comm-gw stores `channel.app_uid` inconsistently (sometimes the app's
 * `app_code_name`, sometimes its numeric `app_id`, sometimes its `uid`), and
 * the messaging pipeline (connect/comm-gw) uses the lowercase canonical
 * code_name (e.g. `instagramdm`) while Core's marketplace app can carry a
 * differently-cased `app_code_name` (e.g. `instagramDM`). Match on any of the
 * app's identifiers, case-insensitively, so a connected channel reliably
 * attaches to its market app instead of being dropped from the list.
 */
const channelMatchesApp = (channel, app) => {
  if (!channel?.app_uid || !app) return false;
  const channelAppUid = channel.app_uid.toLowerCase();
  return channelAppUid === String(app.app_code_name || '').toLowerCase()
    || channelAppUid === String(app.app_id || '').toLowerCase()
    || channelAppUid === String(app.uid || '').toLowerCase();
};

// `GET /v3/license/offerings` is auto-scoped for a staff token to the caller's
// directory offerings plus the business's active-subscription offerings, so no
// directory_uid is needed. `is_listed=true` drops offerings the directory chose to
// hide from its App Market.
const APP_OFFERINGS_QUERY = 'type=app&is_active=true&is_listed=true&page=1&per_page=100';

/**
 * Payment types a business can act on by itself from the App Market.
 *
 * Deliberately excludes:
 *  - `external` — provisioned by the partner through the API. Subscribing requires a
 *    DIRECTORY actor; a staff token gets 403 "You can't subscribe to this offering".
 *  - `single_charge` — same treatment in the market's `pickUsableOffering`.
 *  - `bundle` — obtainable only by buying the package it's bundled into.
 *
 * Mirrors `SUBSCRIPTION_REQUIRED_PAYMENT_TYPES` + the bundle rule in the App Market's
 * appAggregator, which is what makes those apps absent from the market entirely.
 */
const SELF_SERVE_PAYMENT_TYPES = ['monthly', 'annually', 'free'];

/**
 * An app counts as installed when it's explicitly installed OR entitled through the
 * business's package (`internal`) — same rule as `AppsStore/isAppInstalledOrEntitled`.
 * A package-entitled app has no Install step left to take, so it must render as
 * installed (and keep its channels) rather than offering an Install button.
 */
const isAppInstalledOrEntitled = (app) => app?.status === 'installed'
  || Boolean(app?.assignment_types?.includes(ASSIGNMENT_TYPE.INTERNAL));

/**
 * SKUs (app_code_names) the business can install itself, out of the listed offerings
 * its directory exposes. Offerings carry the SKU as `SKU` (uppercase).
 */
const selfServeSkus = (offerings) => new Set(
  (Array.isArray(offerings) ? offerings : [])
    .filter((o) => o?.SKU && SELF_SERVE_PAYMENT_TYPES.includes(o.payment_type))
    .map((o) => o.SKU),
);

/**
 * comm-gw supports a fixed set of communication apps, but not every one of them is
 * obtainable by every business, and `AppsStore` loads `v3/apps/apps` unfiltered (the
 * whole platform catalog). Keep an app only when the user can act on it:
 *
 *  - already installed or package-entitled -> always keep, so its channels surface;
 *  - otherwise, only when its directory exposes a listed, self-serve offering for it.
 *
 * `assignment_types` alone is NOT enough: an app sold as an `external` directory
 * offering (partner-provisioned via API) carries `directory_offering` yet never
 * appears in the App Market, so an Install button for it opens a market that doesn't
 * list it. Gating on the offering instead covers both cases.
 *
 * Not mirrored from the market: the per-currency price check (`findOfferingPrice`
 * against the account's geo), which would pull AccountStore currency into this store
 * for a much rarer edge, and the `billing_app` / `MARKET_EXCLUDED_CODES` exclusions,
 * which only cover payment and document apps — never `communication`.
 */
const isAppActionable = (app, installableSkus) => isAppInstalledOrEntitled(app)
  || installableSkus.has(app?.app_code_name);

const appTitle = (app) => app?.app_name || app?.name || app?.app_code_name || '';

const appDescription = (app) => {
  const desc = app?.description;
  if (!desc) return '';
  if (typeof desc === 'string') return desc;
  return desc.short_description || desc.text || '';
};

const appLogo = (app) => app?.description?.logo || app?.logo || app?.icon || '';

const buildAppGroup = (app, channels, groupState) => ({
  key: app.app_code_name || appTitle(app),
  appCodeName: app.app_code_name || '',
  title: appTitle(app),
  description: appDescription(app),
  logo: appLogo(app),
  state: groupState,
  channels,
  intercepted: channels.some((c) => Boolean(c.intercepted_by_receptionist)),
  hasMultiple: channels.length > 1,
});

const sortGroups = (groups) => [...groups].sort((a, b) => {
  const stateDiff = GROUP_STATE_ORDER[a.state] - GROUP_STATE_ORDER[b.state];
  if (stateDiff !== 0) return stateDiff;
  return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
});

const state = {
  channels: [],
  isFetching: false,
  fetchError: null,
  pendingTogglesByUid: {},
  communicationApps: null,
  isFetchingApps: false,
};

const getters = {
  channels: (s) => s.channels,
  isFetching: (s) => s.isFetching,
  fetchError: (s) => s.fetchError,
  pendingTogglesByUid: (s) => s.pendingTogglesByUid,
  /**
   * Channels owned by comm-gw apps (anything that's not the global
   * CoreInternalApp). Sorted alphabetically by `display_name` (then `app_uid`)
   * so the order in the Channels page is stable across refreshes.
   */
  commGwChannels: (s) => [...s.channels]
    .filter((c) => c?.app_uid && c.app_uid !== CORE_INTERNAL_APP_UID)
    .sort((a, b) => {
      const an = (a.display_name || a.app_uid || '').toLowerCase();
      const bn = (b.display_name || b.app_uid || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    }),
  /** True when a toggle is in flight for a given channel uid. */
  isPendingForChannel: (s) => (uid) => Boolean(s.pendingTogglesByUid[uid]),
  /** Communication apps that are available but not yet installed. */
  availableCommunicationApps: (s) => {
    if (!Array.isArray(s.communicationApps)) return [];
    return s.communicationApps.filter((app) => app.status !== 'installed');
  },
  isFetchingApps: (s) => s.isFetchingApps,
  /**
   * Single card label for a channel. We don't cross-reference Core's
   * `externalApplications` cache here because comm-gw's `channels.app_uid`
   * is currently stored inconsistently (apps.uid for some apps, app_code_name
   * for others, even staff_uid in some legacy test rows) and the
   * externalApplications response doesn't expose `uid` anyway. Falling back
   * to `app_uid` is a last resort just so the card never renders empty.
   */
  cardLabelForChannel: () => (channel) => {
    if (!channel) return '';
    return channel.display_name || channel.app_uid || '';
  },
  /**
   * Unified, ordered view model for the redesigned Channels list: one group per
   * communication app, merged with its connected comm-gw channels. Each group
   * carries the data the card needs (title, description, logo, state, channels,
   * master intercept). The Web Chat card is rendered statically by the section
   * (it owns its own i18n copy and is always pinned first).
   *
   * Install status gates the channels: an app that isn't installed is shown as
   * `not_installed` (so the user can install it) WITHOUT its comm-gw channels,
   * even if stale channel rows still exist for it. Channels that match no
   * communication app at all are not surfaced.
   *
   * `fetchCommunicationApps` has already dropped apps the business has no way to
   * obtain (see `isAppActionable`), so every group here is one the user can act on.
   */
  channelGroups: (s) => {
    const apps = Array.isArray(s.communicationApps) ? s.communicationApps : [];
    const commGwChannels = s.channels.filter(
      (c) => c?.app_uid && c.app_uid !== CORE_INTERNAL_APP_UID,
    );

    const groups = apps.map((app) => {
      if (!isAppInstalledOrEntitled(app)) {
        return buildAppGroup(app, [], GROUP_STATE.NOT_INSTALLED);
      }
      const appChannels = commGwChannels.filter((c) => channelMatchesApp(c, app));
      const groupState = appChannels.length ? GROUP_STATE.CONNECTED : GROUP_STATE.INSTALLED;
      return buildAppGroup(app, appChannels, groupState);
    });

    return sortGroups(groups);
  },
};

const mutations = {
  setChannels(state, channels) {
    state.channels = Array.isArray(channels) ? channels : [];
  },
  setIsFetching(state, value) {
    state.isFetching = Boolean(value);
  },
  setFetchError(state, error) {
    state.fetchError = error || null;
  },
  setPendingForChannel(state, uid) {
    if (!uid) return;
    state.pendingTogglesByUid = { ...state.pendingTogglesByUid, [uid]: true };
  },
  clearPendingForChannel(state, uid) {
    if (!uid) return;
    const next = { ...state.pendingTogglesByUid };
    delete next[uid];
    state.pendingTogglesByUid = next;
  },
  setCommunicationApps(state, apps) {
    state.communicationApps = Array.isArray(apps) ? apps : [];
  },
  setIsFetchingApps(state, value) {
    state.isFetchingApps = Boolean(value);
  },
  /**
   * Replace a single channel's fields (used for optimistic intercept toggle
   * and again on the server response so the store reflects the truth).
   */
  updateChannelLocally(state, channel) {
    if (!channel?.uid) return;
    state.channels = state.channels.map((c) => (
      c.uid === channel.uid ? { ...c, ...channel } : c
    ));
  },
};

const actions = {
  async fetchChannels({ commit }) {
    commit('setIsFetching', true);
    commit('setFetchError', null);
    try {
      const list = await channelsService.listChannels();
      commit('setChannels', list);
      return list;
    } catch (err) {
      logError('Failed to fetch comm-gw channels for AI Receptionist', err);
      const status = err?.response?.status;
      const message = status === 403
        ? 'web_ai_widget.channels.error_forbidden'
        : err?.message || 'web_ai_widget.channels.error_load';
      commit('setFetchError', message);
      commit('setChannels', []);
      throw err;
    } finally {
      commit('setIsFetching', false);
    }
  },

  /**
   * Optimistic intercept toggle: flips local state immediately so the UI feels
   * instant, then issues PUT /channels/:uid. On failure, reverts and surfaces
   * the error to the caller (so the page can show a toast).
   */
  async setIntercept({ commit, state }, { uid, intercepted }) {
    if (!uid) {
      throw new Error('channel uid is required');
    }
    const before = state.channels.find((c) => c.uid === uid) || null;
    if (!before) {
      throw new Error(`channel [${uid}] not found in store`);
    }
    const desired = Boolean(intercepted);
    commit('updateChannelLocally', { uid, intercepted_by_receptionist: desired });
    commit('setPendingForChannel', uid);
    try {
      const updated = await channelsService.setIntercept(uid, desired);
      if (updated) {
        commit('updateChannelLocally', updated);
      }
      return updated;
    } catch (err) {
      logError('Failed to toggle channel intercept', err, { uid, intercepted: desired });
      commit('updateChannelLocally', before);
      throw err;
    } finally {
      commit('clearPendingForChannel', uid);
    }
  },

  /**
   * Master intercept toggle for an app group: fan out `setIntercept` across all
   * of the app's connected channels. Each per-channel call reverts itself on
   * failure; we re-throw the first error so the caller can surface one toast.
   */
  async setAppIntercept({ dispatch }, { channelUids, intercepted }) {
    const uids = Array.isArray(channelUids) ? channelUids.filter(Boolean) : [];
    if (!uids.length) return;
    const results = await Promise.allSettled(
      uids.map((uid) => dispatch('setIntercept', { uid, intercepted })),
    );
    const firstFailure = results.find((r) => r.status === 'rejected');
    if (firstFailure) throw firstFailure.reason;
  },

  /**
   * Listed, self-serve app offerings this business's directory exposes, as a Set of
   * SKUs. Returns null when the offerings call fails, which the caller treats as
   * "unknown" rather than "nothing is installable" — losing this list must not empty
   * the Channels page for a business whose apps are perfectly installable.
   */
  async fetchInstallableAppSkus() {
    try {
      const res = await subscriptionService.getOfferings(APP_OFFERINGS_QUERY);
      return selfServeSkus(res?.offerings);
    } catch (err) {
      logError('Failed to fetch app offerings for the Channels page', err);
      return null;
    }
  },

  async fetchCommunicationApps({ commit, dispatch, rootGetters }, { force = false } = {}) {
    commit('setIsFetchingApps', true);
    try {
      const [, installableSkus] = await Promise.all([
        dispatch('AppsStore/loadApps', { force }, { root: true }),
        dispatch('fetchInstallableAppSkus'),
      ]);
      const commApps = rootGetters['AppsStore/appsByType'](APP_TYPE.COMMUNICATION);
      // No offerings list -> keep every comm app rather than hide the whole page.
      const apps = installableSkus
        ? commApps.filter((app) => isAppActionable(app, installableSkus))
        : commApps;
      commit('setCommunicationApps', apps);
      return apps;
    } catch (err) {
      logError('Failed to fetch communication apps', err);
      commit('setCommunicationApps', []);
      return [];
    } finally {
      commit('setIsFetchingApps', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
